/**
 * Removes team invite tokens from anything sent to Sentry.
 *
 * An invite link, `/teams/invites/<token>`, is a credential: any signed-in
 * user holding the token can join the team and read what it is shared. The
 * path reaches Sentry in error `request.url`, transaction names, navigation
 * breadcrumbs and replay recordings, and the sign-in page carries it again,
 * URL-encoded, in `?returnTo=`. Every one of those is rewritten to a
 * placeholder before it leaves the browser.
 */

const INVITE_PATH = /(\/teams\/invites\/)[^/?#&\s"'<>]+/g;
const ENCODED_INVITE_PATH = /(%2Fteams%2Finvites%2F)[^&#%\s"'<>]+/gi;

export const scrubInviteTokens = (text: string): string =>
  text
    .replace(INVITE_PATH, "$1:token")
    .replace(ENCODED_INVITE_PATH, "$1%3Atoken");

/** Returns [value] with every string inside it scrubbed. Plain data only, as Sentry events are. */
export const scrubDeep = <T>(value: T, seen = new WeakSet<object>()): T => {
  if (typeof value === "string") {
    return scrubInviteTokens(value) as unknown as T;
  }
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (seen.has(value as object)) {
    return value;
  }
  seen.add(value as object);
  if (Array.isArray(value)) {
    return value.map((item) => scrubDeep(item, seen)) as unknown as T;
  }
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    result[key] = scrubDeep(item, seen);
  }
  return result as T;
};
